import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input, Spin } from 'antd';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import logo from '@/assets/Orino.png';
import useLogin from '@/hooks/auth/useLogin';
import { authLoading, loginSuccess } from '@/redux/auth/authSlice';
import { useAppDispatch } from '@/redux/hooks';
import toaster from '@/toaster';
import { useState } from 'react';

const Login = () => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [error, setError] = useState<{
    message: string;
    description: string;
  } | null>(null);
  const { loginUser, isPending } = useLogin();

  const onFinish = (values: { email: string; password: string }) => {
    dispatch(authLoading());
    loginUser(values).then((data) => {
      dispatch(
        loginSuccess({
          tokens: {
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
          },
          user: {
            _id: data._id,
            country: data.country,
            email: data.email,
            name: data.name,
            role: data.role,
            avatar: data.avatar,
            subscriptionStatus: data.subscriptionStatus,
            subscriptionType: data.subscriptionType,
            subscriptionStartDate: data.subscriptionStartDate,
            subscriptionEndDate: data.subscriptionEndDate,
            trialPeriod: data.trialPeriod,
            userStores: data.userStores,
            pin: data.pin,
          },
        })
      );
      toaster.success(`${t('welcome')} 👋`);
      setError(null);
      navigate('/');
    });
  };

  const isArabic = i18n.language === 'ar';

  return (
    <section
      className={`flex flex-col md:flex-row h-screen ${isArabic ? 'md:flex-row-reverse' : 'md:flex-row'}`}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Login Form Container */}
      <div className="flex-1 flex flex-col justify-center items-center bg-gray-100 p-4 md:p-8">
        <div className="w-full max-w-md px-4">
          <Spin spinning={isPending}>
            <h2 className="text-2xl font-bold mb-6 text-center">{t('signIn')}</h2>
            {error && (
              <Alert
                message={error.message}
                type="error"
                description={error.description}
                showIcon
                closable
                className="mb-4"
              />
            )}
            <Form name="login" layout="vertical" initialValues={{}} onFinish={onFinish}>
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: t('emailRequired'),
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined className="text-gray-400" />}
                  type="email"
                  placeholder={t('email')}
                  className="w-full"
                />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: t('passwordRequired'),
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className="text-gray-400" />}
                  placeholder={t('password')}
                  className="w-full"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="w-full bg-primary-start hover:bg-primary-middle"
                >
                  {t('signIn')}
                </Button>
              </Form.Item>
            </Form>

            {/* Register Link */}
            <div className="text-center mt-4">
              <span className="text-gray-600">{t('noAccount')} </span>
              <Link
                to="/register"
                className="text-primary-start hover:text-primary-middle font-semibold"
              >
                {t('registerHere')}
              </Link>
            </div>

            {/* Forgot Password Link */}
            <div className="text-center mt-4">
              <span className="text-gray-600">{t('forgotPassword')} </span>
              <Link
                to="/forget-password"
                className="text-primary-start hover:text-primary-middle font-semibold"
              >
                {t('resetPassword')}
              </Link>
            </div>
          </Spin>
        </div>
      </div>

      {/* Welcome Section */}
      <div className="hidden md:flex flex-1 justify-center items-center bg-primary-start p-8">
        <img src={logo} alt="NORT" className="w-full h-full object-contain" />
      </div>
    </section>
  );
};

export default Login;
